import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { playerAPI, paceDataAPI, teeSheetAPI, tournamentAPI } from '../services/api';
import ColumnMappingModal from '../components/ColumnMappingModal';
import TimeRequestSelector from '../components/TimeRequestSelector';
import { formatTimeRequestForDisplay, convertLegacyTimePreference, TIME_REQUEST_TYPES } from '../utils/timeConstants';
import './TournamentRoster.css';

function TournamentRoster() {
  const { tournamentId } = useParams();
  const { tournament, setTournament, setOnEditTournament } = useOutletContext();
  const [players, setPlayers] = useState([]);
  const [meritData, setMeritData] = useState([]);
  const [paceData, setPaceData] = useState([]);
  const [teeSheet, setTeeSheet] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('handicap');
  const [sortDirection, setSortDirection] = useState('asc');
  const [processingPlayers, setProcessingPlayers] = useState(new Set());
  const [tournamentType, setTournamentType] = useState('individual');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tournament editing state
  const [showTournamentEdit, setShowTournamentEdit] = useState(false);
  const [editingTournament, setEditingTournament] = useState({});
  
  // Typeahead state for pairing partner search
  const [pairingSearchTerm, setPairingSearchTerm] = useState('');
  const [showPairingDropdown, setShowPairingDropdown] = useState(false);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Column mapping modal state
  const [showColumnMappingModal, setShowColumnMappingModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Process requests state
  const [isProcessingRequests, setIsProcessingRequests] = useState(false);
  const [processResults, setProcessResults] = useState(null);
  const [showProcessResults, setShowProcessResults] = useState(false);

  // Preview changes state (for selective apply)
  const [previewChanges, setPreviewChanges] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedChanges, setSelectedChanges] = useState({});
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);

  // Single player parsing state
  const [isParsingRequest, setIsParsingRequest] = useState(false);

  // Roster status management state
  const [rosterSummary, setRosterSummary] = useState({
    player: 0, waitlisted: 0, support_staff: 0, late_drop: 0, no_show: 0, total: 0
  });
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Handicap refresh state
  const [isRefreshingHandicaps, setIsRefreshingHandicaps] = useState(false);

  useEffect(() => {
    if (tournamentId !== 'demo') {
      loadRosterData();
    } else {
      loadDemoData();
    }
  }, [tournamentId]);

  // Set up the edit tournament handler
  useEffect(() => {
    if (setOnEditTournament) {
      setOnEditTournament(() => handleEditTournament);
    }
    
    // Cleanup function to clear the handler when component unmounts
    return () => {
      if (setOnEditTournament) {
        setOnEditTournament(null);
      }
    };
  }, [setOnEditTournament]);

  const loadRosterData = async () => {
    try {
      setIsLoading(true);

      // Load all required data including roster summary
      const [playersResponse, summaryResponse] = await Promise.all([
        playerAPI.getAll(tournamentId),
        playerAPI.getRosterSummary(tournamentId).catch(() => ({ data: null }))
      ]);

      setPlayers(playersResponse.data);

      // Update roster summary if available
      if (summaryResponse.data) {
        setRosterSummary(summaryResponse.data);
      }

      // Extract merit data from player records (populated from Member records)
      const meritData = playersResponse.data.map(player => {
        if (player.orderOfMerit && player.orderOfMerit.effectiveRank) {
          return {
            ghin: player.ghin,
            oomRank: player.orderOfMerit.effectiveRank,
            playerName: player.name,
            effectivePoints: player.orderOfMerit.effectivePoints,
            currentYearRank: player.orderOfMerit.currentYearRank,
            currentYearPoints: player.orderOfMerit.currentYearPoints
          };
        } else {
          return {
            ghin: player.ghin,
            oomRank: 999,
            playerName: player.name,
            effectivePoints: null,
            currentYearRank: null,
            currentYearPoints: null
          };
        }
      });
      setMeritData(meritData);

      // Extract pace data from players who have paceData.behindRank
      const paceData = playersResponse.data.map(player => {
        if (player.paceData && player.paceData.behindRank) {
          return {
            ghin: player.ghin,
            rank: player.paceData.behindRank,
            playerName: player.name,
            averageBehind: player.paceData.averageBehindGroup,
            totalRounds: player.paceData.totalRounds,
            paceRating: player.paceData.paceRating
          };
        } else {
          return {
            ghin: player.ghin,
            rank: 999,
            playerName: player.name,
            averageBehind: null,
            totalRounds: 0,
            paceRating: 'unknown'
          };
        }
      });
      setPaceData(paceData);

      // Try to load existing tee sheet, if none exists, generate one
      try {
        const teeSheetResponse = await teeSheetAPI.getByTournamentId(tournamentId);
        if (teeSheetResponse.data.success) {
          setTeeSheet(teeSheetResponse.data.data);
        }
      } catch (err) {
        // No saved tee sheet exists - group assignments won't be shown
        console.log('No saved tee sheet found for group assignments');
      }

    } catch (err) {
      console.error('Error loading roster data:', err);
      setError('Failed to load roster data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDemoData = () => {
    // Demo data for demonstration
    const demoPlayers = [
      { id: 1, name: 'John Smith', handicapIndex: 8.5, ghin: '12345678', timeRequest: 'early', partnerRequest: 'Mike Johnson' },
      { id: 2, name: 'Mike Johnson', handicapIndex: 12.3, ghin: '23456789', timeRequest: null, partnerRequest: 'John Smith' },
      { id: 3, name: 'David Wilson', handicapIndex: 6.2, ghin: '34567890', timeRequest: 'late', partnerRequest: null },
    ];
    
    setPlayers(demoPlayers);
    setMeritData([]);
    setPaceData([]);
    setIsLoading(false);
  };

  // Tournament editing handlers
  const handleEditTournament = () => {
    setEditingTournament({
      name: tournament?.name || '',
      status: tournament?.status || 'draft',
      feedbackDeadline: tournament?.feedbackDeadline || '',
      feedbackGracePeriodMinutes: tournament?.feedbackGracePeriodMinutes || 15
    });
    setShowTournamentEdit(true);
  };

  const handleSaveTournamentSettings = async () => {
    try {
      const updateData = {
        name: editingTournament.name?.trim() || tournament?.name,
        status: editingTournament.status,
        feedbackDeadline: editingTournament.feedbackDeadline || null,
        feedbackGracePeriodMinutes: parseInt(editingTournament.feedbackGracePeriodMinutes) || 15
      };

      const response = await tournamentAPI.update(tournamentId, updateData);
      if (response.data) {
        setTournament(response.data);
        setShowTournamentEdit(false);
      }
    } catch (err) {
      console.error('Error updating tournament:', err);
    }
  };

  const handleCancelTournamentEdit = () => {
    setShowTournamentEdit(false);
  };

  // Import handlers
  const handleOpenImportModal = () => {
    setImportFile(null);
    setImportError(null);
    setShowImportModal(true);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportError(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        '.xlsx',
        '.xls'
      ];
      const isValidType = validTypes.some(type =>
        file.type === type || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
      );

      if (!isValidType) {
        setImportError('Please select an Excel file (.xlsx or .xls)');
        setImportFile(null);
        return;
      }
      setImportError(null);
      setImportFile(file);
    }
  };

  const handleReimport = async () => {
    if (!importFile) {
      setImportError('Please select a file to import');
      return;
    }

    setIsImporting(true);
    setImportError(null);

    try {
      const response = await playerAPI.reimport(tournamentId, importFile);

      if (response.data) {
        // Reload the roster data
        await loadRosterData();
        setShowImportModal(false);
        setImportFile(null);
        alert(`Successfully imported ${response.data.players?.length || 0} players`);
      }
    } catch (err) {
      console.error('Import error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Failed to import roster';

      // Check if error is about missing columns - offer column mapping
      if (errorMessage.includes('Missing required columns') || errorMessage.includes('column mapping')) {
        setImportError(
          `${errorMessage}\n\nWould you like to manually map the columns?`
        );
      } else {
        setImportError(errorMessage);
      }
    } finally {
      setIsImporting(false);
    }
  };

  // Column mapping handlers
  const handleOpenColumnMapping = async () => {
    if (!importFile) {
      setImportError('Please select a file first');
      return;
    }

    setIsLoadingPreview(true);
    setImportError(null);

    try {
      const response = await playerAPI.previewExcel(importFile, 5);

      if (response.data) {
        setPreviewData(response.data);
        setShowImportModal(false);
        setShowColumnMappingModal(true);
      }
    } catch (err) {
      console.error('Preview error:', err);
      setImportError(err.response?.data?.error || 'Failed to preview Excel file');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleColumnMappingConfirm = async (mappings) => {
    setIsImporting(true);

    try {
      const response = await playerAPI.importWithMapping(tournamentId, importFile, mappings);

      if (response.data) {
        await loadRosterData();
        setShowColumnMappingModal(false);
        setPreviewData(null);
        setImportFile(null);
        alert(`Successfully imported ${response.data.players?.length || 0} players`);
      }
    } catch (err) {
      console.error('Import with mapping error:', err);
      setImportError(err.response?.data?.error || 'Failed to import with column mapping');
      setShowColumnMappingModal(false);
      setShowImportModal(true);
    } finally {
      setIsImporting(false);
    }
  };

  const handleColumnMappingClose = () => {
    setShowColumnMappingModal(false);
    setPreviewData(null);
    setShowImportModal(true);
  };

  // Process Requests handler - now uses preview flow
  const handleProcessRequests = async () => {
    try {
      setIsProcessingRequests(true);

      // Check if this is a team event (would need partner parsing)
      const parsePartners = tournamentType === 'team';

      // First, get a preview of changes
      const response = await playerAPI.previewRequests(tournamentId, { parsePartners });

      if (response.data && response.data.success) {
        const changes = response.data.data.changes || [];

        if (changes.length === 0) {
          alert('No changes detected. All parsed fields match the current values.');
          return;
        }

        // Initialize selected changes - all checked by default
        const initialSelected = {};
        changes.forEach(change => {
          initialSelected[change.playerId] = {
            applyTime: change.timeChanged,
            applyPartner: change.partnerChanged,
            applyPairing: change.pairingChanged
          };
        });

        setPreviewChanges(changes);
        setSelectedChanges(initialSelected);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Error previewing requests:', error);
      alert('Failed to preview player requests: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsProcessingRequests(false);
    }
  };

  // Apply selected changes from preview
  const handleApplySelectedChanges = async () => {
    try {
      setIsApplyingChanges(true);

      // Build the list of changes to apply
      const changesToApply = previewChanges
        .filter(change => {
          const sel = selectedChanges[change.playerId];
          return sel && (sel.applyTime || sel.applyPartner || sel.applyPairing);
        })
        .map(change => ({
          playerId: change.playerId,
          applyTime: selectedChanges[change.playerId].applyTime,
          applyPartner: selectedChanges[change.playerId].applyPartner,
          applyPairing: selectedChanges[change.playerId].applyPairing,
          proposed: change.proposed
        }));

      if (changesToApply.length === 0) {
        alert('No changes selected to apply.');
        return;
      }

      const response = await playerAPI.applySelectedChanges(tournamentId, changesToApply);

      if (response.data && response.data.success) {
        setShowPreviewModal(false);
        setPreviewChanges(null);
        setSelectedChanges({});

        // Show results
        setProcessResults(response.data.data);
        setShowProcessResults(true);

        // Reload players to show updated parsed fields
        await loadRosterData();
      }
    } catch (error) {
      console.error('Error applying changes:', error);
      alert('Failed to apply changes: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsApplyingChanges(false);
    }
  };

  // Toggle individual change selection
  const toggleChangeSelection = (playerId, field) => {
    setSelectedChanges(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: !prev[playerId]?.[field]
      }
    }));
  };

  // Toggle all changes for a player
  const togglePlayerChanges = (playerId, change) => {
    const currentSel = selectedChanges[playerId] || {};
    const allSelected =
      (!change.timeChanged || currentSel.applyTime) &&
      (!change.partnerChanged || currentSel.applyPartner) &&
      (!change.pairingChanged || currentSel.applyPairing);

    setSelectedChanges(prev => ({
      ...prev,
      [playerId]: {
        applyTime: !allSelected && change.timeChanged,
        applyPartner: !allSelected && change.partnerChanged,
        applyPairing: !allSelected && change.pairingChanged
      }
    }));
  };

  // Select/deselect all changes
  const toggleAllChanges = (selectAll) => {
    const newSelected = {};
    previewChanges.forEach(change => {
      newSelected[change.playerId] = {
        applyTime: selectAll && change.timeChanged,
        applyPartner: selectAll && change.partnerChanged,
        applyPairing: selectAll && change.pairingChanged
      };
    });
    setSelectedChanges(newSelected);
  };

  // Refresh handicaps from Member records
  const handleRefreshHandicaps = async () => {
    try {
      setIsRefreshingHandicaps(true);
      const response = await playerAPI.refreshHandicapsFromMembers(tournamentId);

      if (response.data && response.data.success) {
        const { updated, total, notFound } = response.data.data;
        alert(`Updated ${updated} of ${total} handicaps from Member records${notFound > 0 ? ` (${notFound} players not found in member records)` : ''}`);
        await loadRosterData();
      }
    } catch (error) {
      console.error('Error refreshing handicaps:', error);
      alert('Failed to refresh handicaps: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsRefreshingHandicaps(false);
    }
  };

  // Parse single player request handler
  const handleParseSingleRequest = async () => {
    if (!editingPlayer?._id) return;

    try {
      setIsParsingRequest(true);
      const parsePartners = tournamentType === 'team';

      const response = await playerAPI.parseSingleRequest(editingPlayer._id, { parsePartners });

      if (response.data && response.data.success) {
        const { parsed } = response.data.data;

        // Update the editing player with parsed values
        setEditingPlayer(prev => ({
          ...prev,
          parsedTimePreference: parsed.parsedTimePreference,
          parsedPartnerPreference: parsed.parsedPartnerPreference,
          parsedPairingPreference: parsed.parsedPairingPreference,
          selectedPairingPartners: parsed.parsedPairingPreference
            ? parsed.parsedPairingPreference.split(',').map(n => n.trim()).filter(n => n)
            : []
        }));

        // Also update the players list
        setPlayers(prev =>
          prev.map(p => p._id === editingPlayer._id ? {
            ...p,
            parsedTimePreference: parsed.parsedTimePreference,
            parsedPartnerPreference: parsed.parsedPartnerPreference,
            parsedPairingPreference: parsed.parsedPairingPreference
          } : p)
        );
      }
    } catch (error) {
      console.error('Error parsing request:', error);
      alert('Failed to parse request: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsParsingRequest(false);
    }
  };

  // Roster status change handlers
  const handleStatusChange = async (playerId, newStatus) => {
    try {
      await playerAPI.updateRosterStatus(playerId, newStatus, 'user');

      // Update local state
      setPlayers(prev => prev.map(p =>
        p._id === playerId ? { ...p, rosterStatus: newStatus } : p
      ));

      // Reload roster summary
      const summaryResponse = await playerAPI.getRosterSummary(tournamentId);
      if (summaryResponse.data) {
        setRosterSummary(summaryResponse.data);
      }
    } catch (error) {
      console.error('Error updating roster status:', error);
      alert('Failed to update player status: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkStatusChange = async () => {
    if (selectedPlayers.size === 0 || !bulkStatusValue) {
      alert('Please select players and choose a status');
      return;
    }

    try {
      const playerIds = Array.from(selectedPlayers);
      await playerAPI.bulkUpdateRosterStatus(playerIds, bulkStatusValue, 'user', 'Bulk status update');

      // Update local state
      setPlayers(prev => prev.map(p =>
        selectedPlayers.has(p._id) ? { ...p, rosterStatus: bulkStatusValue } : p
      ));

      // Clear selection and reload summary
      setSelectedPlayers(new Set());
      setBulkStatusValue('');
      const summaryResponse = await playerAPI.getRosterSummary(tournamentId);
      if (summaryResponse.data) {
        setRosterSummary(summaryResponse.data);
      }
    } catch (error) {
      console.error('Error updating bulk roster status:', error);
      alert('Failed to update player statuses: ' + (error.response?.data?.message || error.message));
    }
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlayers.size === filteredAndSortedPlayers.length) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(filteredAndSortedPlayers.map(p => p._id)));
    }
  };

  // Status badge styling helper
  const getStatusBadgeStyle = (status) => {
    const styles = {
      player: { backgroundColor: '#28a745', color: 'white' },
      waitlisted: { backgroundColor: '#ffc107', color: '#212529' },
      support_staff: { backgroundColor: '#17a2b8', color: 'white' },
      late_drop: { backgroundColor: '#fd7e14', color: 'white' },
      no_show: { backgroundColor: '#dc3545', color: 'white' }
    };
    return {
      ...styles[status] || styles.player,
      padding: '3px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      display: 'inline-block',
      textTransform: 'uppercase'
    };
  };

  const getStatusLabel = (status) => {
    const labels = {
      player: 'Player',
      waitlisted: 'Waitlisted',
      support_staff: 'Staff',
      late_drop: 'Late Drop',
      no_show: 'No Show'
    };
    return labels[status] || status;
  };

  // Helper function to get merit rank for a player
  const getMeritRank = (ghin) => {
    const merit = meritData?.find(m => m.ghin === ghin);
    return merit?.oomRank || 'N/A';
  };

  // Helper function to get pace rank for a player
  const getPaceRank = (ghin) => {
    const pace = paceData?.find(p => p.ghin === ghin);
    return pace?.rank || 'N/A';
  };

  // Helper function to get current group assignment
  const getCurrentGroup = (ghin) => {
    if (!teeSheet?.groups) return null;
    
    for (let i = 0; i < teeSheet.groups.length; i++) {
      const group = teeSheet.groups[i];
      const playerInGroup = group.players?.find(p => p.ghin === ghin);
      if (playerInGroup) {
        return {
          groupNumber: group.id,
          teeTime: group.teeTime,
          position: group.players.indexOf(playerInGroup) + 1
        };
      }
    }
    return null;
  };

  const handlePlayerUpdate = async (updatedPlayer) => {
    try {
      setProcessingPlayers(prev => new Set(prev).add(updatedPlayer._id));

      // Generate display text from structured time request for backward compatibility
      const timeDisplayText = updatedPlayer.structuredTimeRequest?.type !== 'none'
        ? formatTimeRequestForDisplay(updatedPlayer.structuredTimeRequest)
        : '';

      await playerAPI.update(updatedPlayer._id, {
        timePreference: updatedPlayer.timePreference,
        rawPartnerRequest: updatedPlayer.rawPartnerRequest,
        originalPairingRequest: updatedPlayer.originalPairingRequest,
        notes: updatedPlayer.notes,
        parsedTimePreference: timeDisplayText || updatedPlayer.parsedTimePreference,
        parsedPartnerPreference: updatedPlayer.parsedPartnerPreference,
        parsedPairingPreference: updatedPlayer.parsedPairingPreference,
        selectedPairingPartners: updatedPlayer.selectedPairingPartners || [],
        structuredTimeRequest: updatedPlayer.structuredTimeRequest || { type: 'none' }
      });

      setPlayers(prev => 
        prev.map(p => p._id === updatedPlayer._id ? updatedPlayer : p)
      );
      
      setEditingPlayer(null);
    } catch (error) {
      console.error('Error updating player:', error);
      alert('Failed to update player. Please try again.');
    } finally {
      setProcessingPlayers(prev => {
        const updated = new Set(prev);
        updated.delete(updatedPlayer._id);
        return updated;
      });
    }
  };

  const handleEditPlayer = (player) => {
    // Parse existing pairing preference back to array for typeahead
    const existingPairingPartners = player.parsedPairingPreference ?
      player.parsedPairingPreference.split(',').map(name => name.trim()).filter(name => name) :
      [];

    // Initialize structuredTimeRequest from existing data or convert legacy text
    let structuredTimeRequest = player.structuredTimeRequest;
    if (!structuredTimeRequest || !structuredTimeRequest.type) {
      // Try to convert legacy parsedTimePreference text to structured format
      if (player.parsedTimePreference) {
        structuredTimeRequest = convertLegacyTimePreference(player.parsedTimePreference);
      } else {
        structuredTimeRequest = { type: TIME_REQUEST_TYPES.NONE };
      }
    }

    setEditingPlayer({
      ...player,
      selectedPairingPartners: existingPairingPartners,
      structuredTimeRequest
    });

    // Clear typeahead state
    setPairingSearchTerm('');
    setShowPairingDropdown(false);
  };

  const handleCancelEdit = () => {
    setEditingPlayer(null);
  };

  // Filter and sort players
  const filteredAndSortedPlayers = players
    .filter(player => {
      // Status filter
      if (statusFilter !== 'all' && player.rosterStatus !== statusFilter) {
        return false;
      }
      // Search filter
      return player.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.ghin?.includes(searchTerm);
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'handicap':
          aValue = a.handicapIndex || 0;
          bValue = b.handicapIndex || 0;
          break;
        case 'merit':
          aValue = getMeritRank(a.ghin);
          bValue = getMeritRank(b.ghin);
          aValue = aValue === 'N/A' ? 999 : aValue;
          bValue = bValue === 'N/A' ? 999 : bValue;
          break;
        case 'pace':
          aValue = getPaceRank(a.ghin);
          bValue = getPaceRank(b.ghin);
          aValue = aValue === 'N/A' ? 999 : aValue;
          bValue = bValue === 'N/A' ? 999 : bValue;
          break;
        default:
          aValue = a[sortBy] || '';
          bValue = b[sortBy] || '';
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  if (isLoading) {
    return (
      <div className="tournament-roster loading">
        <div className="loading-spinner">Loading roster...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tournament-roster error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="tournament-roster">
      <div className="roster-header">
        <div className="roster-info">
          <h2>Player Roster</h2>
          <p className="roster-count">{players.length} players registered</p>
        </div>

        <div className="roster-actions">
          <button
            className="btn btn-primary"
            onClick={handleOpenImportModal}
          >
            Import Roster
          </button>
          <button
            className="btn btn-outline"
            onClick={handleProcessRequests}
            disabled={isProcessingRequests || players.length === 0}
            style={{ marginLeft: '8px' }}
          >
            {isProcessingRequests ? 'Processing...' : 'Process Requests'}
          </button>
          <button
            className="btn btn-outline"
            onClick={handleRefreshHandicaps}
            disabled={isRefreshingHandicaps || players.length === 0}
            style={{ marginLeft: '8px' }}
            title="Refresh handicaps from Member records"
          >
            {isRefreshingHandicaps ? 'Refreshing...' : 'Refresh Handicaps'}
          </button>
        </div>

        <div className="roster-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ marginRight: '8px' }}
          >
            <option value="all">All Statuses</option>
            <option value="player">Players Only</option>
            <option value="waitlisted">Waitlisted</option>
            <option value="support_staff">Support Staff</option>
            <option value="late_drop">Late Drops</option>
            <option value="no_show">No Shows</option>
          </select>

          <select
            value={`${sortBy}-${sortDirection}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('-');
              setSortBy(field);
              setSortDirection(direction);
            }}
          >
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="handicap-asc">Handicap Low-High</option>
            <option value="handicap-desc">Handicap High-Low</option>
            <option value="merit-asc">Merit Rank 1st-Last</option>
            <option value="merit-desc">Merit Rank Last-1st</option>
            <option value="pace-asc">Pace Rank 1st-Last</option>
            <option value="pace-desc">Pace Rank Last-1st</option>
          </select>
        </div>
      </div>

      {/* Roster Summary Bar */}
      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '12px 16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{ ...getStatusBadgeStyle('player'), cursor: 'pointer' }} onClick={() => setStatusFilter('player')}>
          {rosterSummary.player} Players
        </span>
        <span style={{ ...getStatusBadgeStyle('waitlisted'), cursor: 'pointer' }} onClick={() => setStatusFilter('waitlisted')}>
          {rosterSummary.waitlisted} Waitlisted
        </span>
        <span style={{ ...getStatusBadgeStyle('support_staff'), cursor: 'pointer' }} onClick={() => setStatusFilter('support_staff')}>
          {rosterSummary.support_staff} Staff
        </span>
        <span style={{ ...getStatusBadgeStyle('late_drop'), cursor: 'pointer' }} onClick={() => setStatusFilter('late_drop')}>
          {rosterSummary.late_drop} Late Drops
        </span>
        <span style={{ ...getStatusBadgeStyle('no_show'), cursor: 'pointer' }} onClick={() => setStatusFilter('no_show')}>
          {rosterSummary.no_show} No Shows
        </span>
        {statusFilter !== 'all' && (
          <button
            onClick={() => setStatusFilter('all')}
            style={{
              background: 'none',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              padding: '2px 8px',
              fontSize: '12px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            Clear Filter
          </button>
        )}

      </div>

      {/* Bulk Status Change Bar */}
      {selectedPlayers.size > 0 && (
        <div style={{
          display: 'flex',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: '#e3f2fd',
          borderRadius: '8px',
          marginBottom: '16px',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: '600' }}>{selectedPlayers.size} selected</span>
          <select
            value={bulkStatusValue}
            onChange={(e) => setBulkStatusValue(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="">Change status to...</option>
            <option value="player">Player</option>
            <option value="waitlisted">Waitlisted</option>
            <option value="support_staff">Support Staff</option>
            <option value="late_drop">Late Drop</option>
            <option value="no_show">No Show</option>
          </select>
          <button
            onClick={handleBulkStatusChange}
            disabled={!bulkStatusValue}
            className="btn btn-primary btn-sm"
            style={{ padding: '6px 16px' }}
          >
            Apply
          </button>
          <button
            onClick={() => setSelectedPlayers(new Set())}
            className="btn btn-outline btn-sm"
            style={{ padding: '6px 16px' }}
          >
            Clear Selection
          </button>
        </div>
      )}

      <div className="roster-table-container">
        <table className="roster-table">
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={selectedPlayers.size > 0 && selectedPlayers.size === filteredAndSortedPlayers.length}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </th>
              <th>Name</th>
              <th>Status</th>
              <th>GHIN</th>
              <th>Handicap Index</th>
              <th>Merit Rank</th>
              <th>Pace Rank</th>
              <th>Time Request</th>
              <th>Partner Request</th>
              <th>Pairing Request</th>
              <th>Group Assignment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedPlayers.map(player => {
              const groupAssignment = getCurrentGroup(player.ghin);
              const isProcessing = processingPlayers.has(player._id);

              return (
                <tr key={player._id || player.id} className={isProcessing ? 'processing' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedPlayers.has(player._id)}
                      onChange={() => togglePlayerSelection(player._id)}
                    />
                  </td>
                  <td className="player-name">
                    {player.name}
                    {player.originalPairingRequest && (
                      <span
                        className="request-badge"
                        title={`Original Request: ${player.originalPairingRequest}`}
                        style={{
                          marginLeft: '6px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          borderRadius: '10px',
                          cursor: 'help'
                        }}
                      >
                        REQ
                      </span>
                    )}
                    {player.manualOverride?.isOverridden && (
                      <span className="override-indicator" title="Manual placement override">
                        📌
                      </span>
                    )}
                  </td>
                  <td>
                    <select
                      value={player.rosterStatus || 'player'}
                      onChange={(e) => handleStatusChange(player._id, e.target.value)}
                      style={{
                        ...getStatusBadgeStyle(player.rosterStatus || 'player'),
                        border: 'none',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        paddingRight: '20px',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M2 4l4 4 4-4z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 4px center',
                        backgroundSize: '12px'
                      }}
                    >
                      <option value="player">Player</option>
                      <option value="waitlisted">Waitlisted</option>
                      <option value="support_staff">Staff</option>
                      <option value="late_drop">Late Drop</option>
                      <option value="no_show">No Show</option>
                    </select>
                  </td>
                  <td>{player.ghin}</td>
                  <td>{player.handicapIndex?.toFixed(1) || 'N/A'}</td>
                  <td>{getMeritRank(player.ghin)}</td>
                  <td>{getPaceRank(player.ghin)}</td>
                  <td>
                    {player.structuredTimeRequest?.type && player.structuredTimeRequest.type !== 'none'
                      ? formatTimeRequestForDisplay(player.structuredTimeRequest)
                      : (player.parsedTimePreference || '-')}
                  </td>
                  <td>
                    {player.parsedPartnerPreference || '-'}
                  </td>
                  <td>
                    {player.parsedPairingPreference || '-'}
                  </td>
                  <td>
                    {groupAssignment ? (
                      <span className="group-assignment">
                        Group {groupAssignment.groupNumber} - {groupAssignment.teeTime}
                      </span>
                    ) : (
                      <span className="no-assignment">Not assigned</span>
                    )}
                  </td>
                  <td>
                    <button 
                      onClick={() => handleEditPlayer(player)}
                      disabled={isProcessing}
                      className="btn btn-sm btn-outline"
                    >
                      {isProcessing ? 'Processing...' : 'Edit'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Player Modal */}
      {editingPlayer && (
        <div className="modal-overlay">
          <div className="player-edit-modal">
            <h3>Edit Player: {editingPlayer.name}</h3>
            
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ margin: 0 }}>Original Request:</label>
                <button
                  onClick={handleParseSingleRequest}
                  disabled={isParsingRequest || !editingPlayer.originalPairingRequest}
                  className="btn btn-sm"
                  style={{
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '0.85em',
                    cursor: isParsingRequest || !editingPlayer.originalPairingRequest ? 'not-allowed' : 'pointer',
                    opacity: isParsingRequest || !editingPlayer.originalPairingRequest ? 0.6 : 1
                  }}
                >
                  {isParsingRequest ? 'Parsing...' : 'Parse Request'}
                </button>
              </div>
              <textarea
                value={editingPlayer.originalPairingRequest || editingPlayer.rawPartnerRequest || ''}
                onChange={(e) => setEditingPlayer(prev => ({
                  ...prev,
                  originalPairingRequest: e.target.value,
                  rawPartnerRequest: e.target.value
                }))}
                placeholder="Player's original request text..."
                rows={2}
                style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}
              />
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Enter the player's request and click "Parse Request" to extract time/pairing preferences
              </small>
            </div>

            <div className="form-group">
              <label>Time Preference:</label>
              <TimeRequestSelector
                value={editingPlayer.structuredTimeRequest}
                onChange={(newTimeRequest) => setEditingPlayer(prev => ({
                  ...prev,
                  structuredTimeRequest: newTimeRequest,
                  timePreference: newTimeRequest.type === 'between' && newTimeRequest.startTime === '07:00' ? 'early' :
                                  newTimeRequest.type === 'after' && newTimeRequest.time === '10:00' ? 'late' : 'none'
                }))}
                disabled={false}
              />
            </div>

            <div className="form-group">
              <label>Partner Request:</label>
              <input
                type="text"
                value={editingPlayer.parsedPartnerPreference || ''}
                onChange={(e) => setEditingPlayer(prev => ({
                  ...prev,
                  parsedPartnerPreference: e.target.value
                }))}
                placeholder="Enter partner names"
              />
            </div>

            <div className="form-group">
              <label>Pairing Request:</label>
              
              {/* Selected Partners Display */}
              {editingPlayer.selectedPairingPartners && editingPlayer.selectedPairingPartners.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  {editingPlayer.selectedPairingPartners.map((partner, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        background: '#e3f2fd',
                        color: '#1976d2',
                        padding: '4px 8px',
                        margin: '2px',
                        borderRadius: '4px',
                        fontSize: '0.9em'
                      }}
                    >
                      {partner}
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPartners = editingPlayer.selectedPairingPartners.filter((_, i) => i !== index);
                          setEditingPlayer(prev => ({
                            ...prev,
                            selectedPairingPartners: updatedPartners,
                            parsedPairingPreference: updatedPartners.join(', ')
                          }));
                        }}
                        style={{
                          marginLeft: '4px',
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontSize: '0.8em'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Typeahead Input */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={pairingSearchTerm}
                  onChange={(e) => {
                    setPairingSearchTerm(e.target.value);
                    setShowPairingDropdown(e.target.value.length >= 3);
                  }}
                  onFocus={() => {
                    if (pairingSearchTerm.length >= 3) setShowPairingDropdown(true);
                  }}
                  onBlur={() => {
                    // Delay hiding to allow for clicks
                    setTimeout(() => setShowPairingDropdown(false), 200);
                  }}
                  placeholder="Type 3+ characters to search for players..."
                />
                
                {/* Dropdown Results */}
                {showPairingDropdown && pairingSearchTerm.length >= 3 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    {players
                      .filter(p => 
                        p._id !== editingPlayer._id && // Don't include the player themselves
                        p.name.toLowerCase().includes(pairingSearchTerm.toLowerCase()) &&
                        !(editingPlayer.selectedPairingPartners || []).includes(p.name) // Don't show already selected
                      )
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(player => (
                        <div
                          key={player._id}
                          onClick={() => {
                            const currentPartners = editingPlayer.selectedPairingPartners || [];
                            const updatedPartners = [...currentPartners, player.name];
                            setEditingPlayer(prev => ({
                              ...prev,
                              selectedPairingPartners: updatedPartners,
                              parsedPairingPreference: updatedPartners.join(', ')
                            }));
                            setPairingSearchTerm('');
                            setShowPairingDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          {player.name}
                        </div>
                      ))
                    }
                    {players.filter(p => 
                      p._id !== editingPlayer._id && 
                      p.name.toLowerCase().includes(pairingSearchTerm.toLowerCase()) &&
                      !(editingPlayer.selectedPairingPartners || []).includes(p.name)
                    ).length === 0 && (
                      <div style={{ padding: '8px 12px', color: '#666', fontStyle: 'italic' }}>
                        No players found
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Type 3+ characters to search and select players for pairing
              </small>
            </div>

            <div className="form-group">
              <label>Notes:</label>
              <textarea
                value={editingPlayer.notes || ''}
                onChange={(e) => setEditingPlayer(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button onClick={handleCancelEdit} className="btn btn-outline">
                Cancel
              </button>
              <button 
                onClick={() => handlePlayerUpdate(editingPlayer)}
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tournament Settings Modal */}
      {showTournamentEdit && (
        <div className="modal-overlay">
          <div className="tournament-settings-modal">
            <h3>Tournament Settings</h3>

            <div className="form-group">
              <label htmlFor="tournament-name">Tournament Name:</label>
              <input
                type="text"
                id="tournament-name"
                value={editingTournament.name || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="tournament-status">Status:</label>
              <select
                id="tournament-status"
                value={editingTournament.status || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="draft">Draft</option>
                <option value="processing">Processing</option>
                <option value="published-for-feedback">Published for Feedback</option>
                <option value="completed">Completed</option>
                <option value="finalized">Finalized</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="feedback-deadline">Feedback Deadline:</label>
              <input
                type="datetime-local"
                id="feedback-deadline"
                value={editingTournament.feedbackDeadline || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackDeadline: e.target.value }))}
              />
              <small>Leave empty for no deadline</small>
            </div>

            <div className="form-group">
              <label htmlFor="grace-period">Grace Period (minutes):</label>
              <input
                type="number"
                id="grace-period"
                min="0"
                max="60"
                value={editingTournament.feedbackGracePeriodMinutes || 15}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, feedbackGracePeriodMinutes: e.target.value }))}
              />
              <small>How long to allow feedback after the deadline</small>
            </div>

            <div className="modal-actions">
              <button onClick={handleCancelTournamentEdit} className="cancel-button">
                Cancel
              </button>
              <button onClick={handleSaveTournamentSettings} className="confirm-button">
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Roster Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="player-edit-modal">
            <h3>Import Roster</h3>

            <div className="import-warning" style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <strong>Warning:</strong> This will replace all existing player data, merit rankings, and pace data for this tournament. This action cannot be undone.
            </div>

            <div className="form-group">
              <label htmlFor="import-file">Select Excel File:</label>
              <input
                type="file"
                id="import-file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={isImporting}
              />
              <small>Supported formats: .xlsx, .xls</small>
            </div>

            {importFile && (
              <div style={{ marginBottom: '16px', color: '#28a745' }}>
                Selected: {importFile.name}
              </div>
            )}

            {importError && (
              <div style={{
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                color: '#721c24',
                whiteSpace: 'pre-wrap'
              }}>
                {importError}
                {(importError.includes('Missing required columns') || importError.includes('column mapping')) && (
                  <div style={{ marginTop: '12px' }}>
                    <button
                      onClick={handleOpenColumnMapping}
                      className="btn btn-primary"
                      disabled={isLoadingPreview || !importFile}
                      style={{ background: '#17a2b8', borderColor: '#17a2b8' }}
                    >
                      {isLoadingPreview ? 'Loading...' : 'Use Column Mapping'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={handleCloseImportModal}
                className="btn btn-outline"
                disabled={isImporting || isLoadingPreview}
              >
                Cancel
              </button>
              <button
                onClick={handleOpenColumnMapping}
                className="btn btn-outline"
                disabled={!importFile || isImporting || isLoadingPreview}
                style={{ marginRight: '8px' }}
              >
                {isLoadingPreview ? 'Loading...' : 'Map Columns Manually'}
              </button>
              <button
                onClick={handleReimport}
                className="btn btn-primary"
                disabled={!importFile || isImporting || isLoadingPreview}
              >
                {isImporting ? 'Importing...' : 'Import & Replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Mapping Modal */}
      <ColumnMappingModal
        isOpen={showColumnMappingModal}
        onClose={handleColumnMappingClose}
        previewData={previewData}
        onConfirm={handleColumnMappingConfirm}
        isLoading={isImporting}
      />

      {/* Preview Changes Modal */}
      {showPreviewModal && previewChanges && (
        <div className="modal-overlay">
          <div className="player-edit-modal" style={{ maxWidth: '1000px', maxHeight: '85vh', overflow: 'auto' }}>
            <h3>Preview Request Changes</h3>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              Review the changes below. Uncheck any changes you want to skip. Only checked items will be applied.
            </p>

            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <strong>Summary:</strong> {previewChanges.length} player(s) with changes detected
              <div style={{ marginTop: '8px' }}>
                <button
                  onClick={() => toggleAllChanges(true)}
                  className="btn btn-sm"
                  style={{ marginRight: '8px', padding: '4px 8px', fontSize: '0.85em' }}
                >
                  Select All
                </button>
                <button
                  onClick={() => toggleAllChanges(false)}
                  className="btn btn-sm"
                  style={{ padding: '4px 8px', fontSize: '0.85em' }}
                >
                  Deselect All
                </button>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '30px' }}></th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Player</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Time Pref</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Partner Pref</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Pairing Pref</th>
                </tr>
              </thead>
              <tbody>
                {previewChanges.map((change) => {
                  const sel = selectedChanges[change.playerId] || {};
                  const hasAnyChanges = change.timeChanged || change.partnerChanged || change.pairingChanged;

                  return (
                    <tr key={change.playerId} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '8px', verticalAlign: 'top' }}>
                        <input
                          type="checkbox"
                          checked={hasAnyChanges && (sel.applyTime || sel.applyPartner || sel.applyPairing)}
                          onChange={() => togglePlayerChanges(change.playerId, change)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '8px', fontWeight: '500', verticalAlign: 'top' }}>
                        {change.playerName}
                      </td>

                      {/* Time Preference */}
                      <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                        {change.timeChanged ? (
                          <div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={sel.applyTime || false}
                                onChange={() => toggleChangeSelection(change.playerId, 'applyTime')}
                              />
                              <div style={{ textAlign: 'left' }}>
                                <div style={{ color: '#dc3545', textDecoration: 'line-through', fontSize: '0.9em' }}>
                                  {change.current.time || '(empty)'}
                                </div>
                                <div style={{ color: '#28a745' }}>
                                  {change.proposed.time || '(empty)'}
                                </div>
                              </div>
                            </label>
                          </div>
                        ) : (
                          <span style={{ color: '#6c757d' }}>-</span>
                        )}
                      </td>

                      {/* Partner Preference */}
                      <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                        {change.partnerChanged ? (
                          <div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={sel.applyPartner || false}
                                onChange={() => toggleChangeSelection(change.playerId, 'applyPartner')}
                              />
                              <div style={{ textAlign: 'left' }}>
                                <div style={{ color: '#dc3545', textDecoration: 'line-through', fontSize: '0.9em' }}>
                                  {change.current.partner || '(empty)'}
                                </div>
                                <div style={{ color: '#28a745' }}>
                                  {change.proposed.partner || '(empty)'}
                                </div>
                              </div>
                            </label>
                          </div>
                        ) : (
                          <span style={{ color: '#6c757d' }}>-</span>
                        )}
                      </td>

                      {/* Pairing Preference */}
                      <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                        {change.pairingChanged ? (
                          <div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={sel.applyPairing || false}
                                onChange={() => toggleChangeSelection(change.playerId, 'applyPairing')}
                              />
                              <div style={{ textAlign: 'left', maxWidth: '200px' }}>
                                <div style={{ color: '#dc3545', textDecoration: 'line-through', fontSize: '0.9em', wordBreak: 'break-word' }}>
                                  {change.current.pairing || '(empty)'}
                                </div>
                                <div style={{ color: '#28a745', wordBreak: 'break-word' }}>
                                  {change.proposed.pairing || '(empty)'}
                                </div>
                              </div>
                            </label>
                          </div>
                        ) : (
                          <span style={{ color: '#6c757d' }}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="modal-actions" style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewChanges(null);
                  setSelectedChanges({});
                }}
                className="btn"
                disabled={isApplyingChanges}
              >
                Cancel
              </button>
              <button
                onClick={handleApplySelectedChanges}
                className="btn btn-primary"
                disabled={isApplyingChanges}
              >
                {isApplyingChanges ? 'Applying...' : 'Apply Selected Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Requests Results Modal */}
      {showProcessResults && processResults && (
        <div className="modal-overlay">
          <div className="player-edit-modal" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>Request Processing Results</h3>

            <div style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <strong>Summary:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Processed: {processResults.processed} players</li>
                <li>With Time Preference: {processResults.withTimePreference}</li>
                <li>With Partner Preference: {processResults.withPartnerPreference}</li>
                <li>With Pairing Preference: {processResults.withPairingPreference}</li>
              </ul>
            </div>

            {processResults.details && processResults.details.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '12px' }}>Details:</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Player</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Original Request</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Time</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Partner</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Pairing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processResults.details.map((detail, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '8px' }}>{detail.name}</td>
                        <td style={{ padding: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={detail.original}>
                          {detail.original}
                        </td>
                        <td style={{ padding: '8px', color: detail.parsed.parsedTimePreference ? '#28a745' : '#6c757d' }}>
                          {detail.parsed.parsedTimePreference || '-'}
                        </td>
                        <td style={{ padding: '8px', color: detail.parsed.parsedPartnerPreference ? '#28a745' : '#6c757d' }}>
                          {detail.parsed.parsedPartnerPreference || '-'}
                        </td>
                        <td style={{ padding: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: detail.parsed.parsedPairingPreference ? '#17a2b8' : '#6c757d' }} title={detail.parsed.parsedPairingPreference}>
                          {detail.parsed.parsedPairingPreference || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button
                onClick={() => {
                  setShowProcessResults(false);
                  setProcessResults(null);
                }}
                className="btn btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default TournamentRoster;